// Refonte du suivi — accès aux versions (lot 3).
//
// Une seule porte vers la base pour le nouveau circuit : les RPC suivi_*
// (migrations V97.250-283) et la vue suivi_versions. Chaque erreur est
// traduite en un message court, en français, que l'écran affiche tel quel.
// Aucun appel n'est rejoué en silence : un conflit (PT409) arrête tout et
// demande de recharger.

import { supabase as defaultDb } from '../../supabaseClient';

export const SUIVI_MOTIFS = Object.freeze(['rendez_vous', 'demande', 'changement_phase']);

// Champs qu'un brouillon peut modifier (miroir de suivi_enregistrer_brouillon).
export const CHAMPS_BROUILLON = Object.freeze([
  'nutrition_plan', 'supplements', 'recipes', 'meal_recipes', 'editorial_overrides',
  'fiche_frigo_json', 'intro_letter', 'notes_for_coach', 'private_notes', 'label',
]);
export const CHAMPS_PHASES = Object.freeze(['protocol_phases', 'active_phase_id']);

export const MESSAGE_CONFLIT = 'Cette version a été modifiée dans un autre onglet. Recharge pour continuer.';
export const MESSAGE_RESEAU = 'Connexion perdue. Tes modifications sont gardées : elles partiront dès que la connexion revient.';
export const MESSAGE_DROITS = 'Accès refusé. Reconnecte-toi puis réessaie.';
export const MESSAGE_INCONNU = 'Une erreur est survenue. Réessaie dans un instant.';

/** Erreur du suivi : message lisible + nature (conflit, réseau, refus…). */
export class SuiviErreur extends Error {
  constructor(message, { nature = 'refus', code = null, detail = null, cause = null } = {}) {
    super(message);
    this.name = 'SuiviErreur';
    this.nature = nature;
    this.code = code;
    this.detail = detail;
    this.cause = cause;
  }
}

// Les messages du serveur sont déjà en français mais sans accents (SQL
// ASCII) : on les remplace par une version lisible quand on les connaît.
const MESSAGES_CONNUS = [
  [/brouillon existe deja/i, 'Un brouillon est déjà en cours : il s’ouvre à la place.'],
  [/Termine d abord la version en cours/i, 'Termine d’abord la version en cours : publie-la ou remets-la.'],
  [/rendez-vous n a pas encore ete relu/i, 'Ce rendez-vous n’a pas encore été relu.'],
  [/Rendez-vous de reference introuvable/i, 'Ce rendez-vous est introuvable.'],
  [/pas de phase suivante/i, 'Il n’y a pas de phase suivante à activer.'],
  [/Ce programme n a pas de phases/i, 'Ce programme n’a pas de phases.'],
  [/Programme MGD : le changement de phase/i, 'Programme MGD : le changement de phase n’est pas encore possible ici.'],
  [/Programme MGD : les menus/i, 'Programme MGD : les menus et les phases ne se modifient pas sans nouvelle génération MGD.'],
  [/Programme MGD : une adoption MGD/i, 'Programme MGD : une nouvelle génération MGD est nécessaire pour préparer la version.'],
  [/Seule la derniere version/i, 'Seule la dernière version peut être modifiée.'],
  [/Seul un brouillon de suivi/i, 'Cette version n’est plus un brouillon : elle ne se modifie plus.'],
  [/Version validee : elle ne peut plus/i, 'Cette version est validée : elle ne se modifie plus.'],
  [/Une version suivante existe deja/i, 'Une version plus récente existe : publie plutôt la dernière.'],
  [/Le plan nutritionnel ne peut pas etre vide/i, 'Le plan ne peut pas être vide.'],
  [/Version du nouveau suivi/i, 'Cette version appartient au nouveau suivi.'],
  [/Le controle clinique porte sur un autre contenu|Le contenu a change pendant la verification/i, 'Le contenu a changé pendant le contrôle. Relance le contrôle.'],
  [/Le contenu a change depuis la validation/i, 'Le contenu a changé depuis la validation. Prépare une nouvelle version.'],
  [/La fiche de la cliente a change pendant la verification/i, 'La fiche de la cliente a changé pendant le contrôle. Relance le contrôle.'],
  [/deja validee autrement/i, 'Cette version est déjà validée autrement. Recharge la page.'],
  [/tampon de publication existe deja/i, 'Cette version est déjà publiée dans l’app.'],
  [/Controle clinique bloquant/i, 'Contrôle clinique bloquant : confirme-le consciemment pour continuer.'],
  [/Programme MGD : clairance clinique requise/i, 'Programme MGD : le contrôle clinique doit être sans alerte.'],
  [/Programme MGD : aucune confirmation/i, 'Programme MGD : aucune confirmation consciente n’est possible.'],
  [/provenance serveur du plan/i, 'L’origine du plan n’est plus prouvée : relance la génération du plan.'],
  [/pas destinee a l app/i, 'Cette version n’a pas été validée pour l’app.'],
  [/pas destinee au document/i, 'Cette version n’a pas été validée pour le PDF.'],
  [/Version validee introuvable/i, 'La version validée est introuvable.'],
  [/deja ete remise par l ancien circuit/i, 'Cette version a déjà été remise par l’ancien écran.'],
];

function messageServeurLisible(message) {
  const texte = String(message || '');
  for (const [motif, lisible] of MESSAGES_CONNUS) {
    if (motif.test(texte)) return lisible;
  }
  return texte.trim() || MESSAGE_INCONNU;
}

function estErreurReseau(error) {
  if (!error) return false;
  if (error.code) return false;
  const texte = `${error.name || ''} ${error.message || ''}`;
  return /fetch|network|réseau|offline|Failed to|Load failed|timeout/i.test(texte);
}

/** Traduit une erreur PostgREST / réseau en SuiviErreur. */
export function traduireErreurSuivi(error) {
  if (error instanceof SuiviErreur) return error;
  const code = error?.code ? String(error.code) : null;
  if (code === 'PT409') {
    if (/brouillon existe deja/i.test(error?.message || '')) {
      return new SuiviErreur(messageServeurLisible(error.message), {
        nature: 'brouillon_existant', code, detail: error?.details || null, cause: error,
      });
    }
    // Conflit de révision : message générique. Conflit connu (contenu changé,
    // déjà publiée…) : son message propre, plus utile à Anissa.
    const lisible = messageServeurLisible(error?.message);
    const connu = MESSAGES_CONNUS.some(([motif]) => motif.test(String(error?.message || '')));
    return new SuiviErreur(connu ? lisible : MESSAGE_CONFLIT, { nature: 'conflit', code, detail: error?.details || null, cause: error });
  }
  if (code === '42501') {
    return new SuiviErreur(MESSAGE_DROITS, { nature: 'droits', code, cause: error });
  }
  if (estErreurReseau(error)) {
    return new SuiviErreur(MESSAGE_RESEAU, { nature: 'reseau', cause: error });
  }
  if (code === '23514' || code === '22023') {
    return new SuiviErreur(messageServeurLisible(error?.message), { nature: 'refus', code, cause: error });
  }
  return new SuiviErreur(messageServeurLisible(error?.message), { nature: 'inconnu', code, cause: error });
}

async function appeler(db, nom, params) {
  if (!db?.rpc) throw new SuiviErreur(MESSAGE_RESEAU, { nature: 'reseau' });
  let reponse;
  try {
    reponse = await db.rpc(nom, params);
  } catch (error) {
    throw traduireErreurSuivi(error);
  }
  if (reponse?.error) throw traduireErreurSuivi(reponse.error);
  return reponse?.data ?? null;
}

const uneLigne = (data) => (Array.isArray(data) ? (data[0] ?? null) : data);

/** Ne garde que les champs autorisés qui ont réellement changé. */
export function changementsBrouillon(avant, apres, { phases = false } = {}) {
  const champs = phases ? [...CHAMPS_BROUILLON, ...CHAMPS_PHASES] : CHAMPS_BROUILLON;
  const changes = {};
  for (const champ of champs) {
    if (!apres || !Object.hasOwn(apres, champ)) continue;
    const valeur = apres[champ] === undefined ? null : apres[champ];
    const ancienne = avant?.[champ] === undefined ? null : avant?.[champ];
    if (JSON.stringify(valeur) !== JSON.stringify(ancienne)) changes[champ] = valeur;
  }
  return changes;
}

/** Le drapeau suivi_v2 (app_config) : liste d'identifiants de clientes. */
// Formes acceptées (lot 9, docs/BASCULE-SUIVI-V2.md) :
//   ["id", …] ou {"clientes":["id", …]} : ces clientes seulement (essai) ;
//   {"tous":true,"sauf":["id", …]} : toutes les clientes, sauf celles listées
//   (après l'essai). Toute autre valeur, ou illisible = écran actuel.
export function lireDrapeauDepuisValeur(valeur, clientId) {
  if (!clientId || typeof valeur !== 'string' || !valeur.trim()) return false;
  try {
    const parsed = JSON.parse(valeur);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.tous === true) {
      // « sauf » absent = personne d'exclu ; présent mais pas une liste = illisible, fermé.
      if (parsed.sauf !== undefined && !Array.isArray(parsed.sauf)) return false;
      const sauf = parsed.sauf || [];
      return !sauf.some((id) => String(id) === String(clientId));
    }
    const liste = Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.clientes) ? parsed.clientes : []);
    return liste.some((id) => String(id) === String(clientId));
  } catch {
    return false;
  }
}

export async function lireDrapeauSuiviV2(clientId, { db = defaultDb } = {}) {
  if (!db?.from || !clientId) return false;
  try {
    const { data, error } = await db.from('app_config').select('value').eq('key', 'suivi_v2').maybeSingle();
    if (error) return false;
    return lireDrapeauDepuisValeur(data?.value, clientId);
  } catch {
    return false;
  }
}

export async function lireVersions(clientId, { db = defaultDb } = {}) {
  if (!db?.from) throw new SuiviErreur(MESSAGE_RESEAU, { nature: 'reseau' });
  let reponse;
  try {
    reponse = await db.from('suivi_versions').select('*').eq('client_id', clientId).order('rang', { ascending: true });
  } catch (error) {
    throw traduireErreurSuivi(error);
  }
  if (reponse?.error) throw traduireErreurSuivi(reponse.error);
  return Array.isArray(reponse?.data) ? reponse.data : [];
}

export async function lireVersion(consultationId, { db = defaultDb } = {}) {
  if (!db?.from) throw new SuiviErreur(MESSAGE_RESEAU, { nature: 'reseau' });
  let reponse;
  try {
    reponse = await db.from('nutrition_consultations').select('*').eq('id', consultationId).maybeSingle();
  } catch (error) {
    throw traduireErreurSuivi(error);
  }
  if (reponse?.error) throw traduireErreurSuivi(reponse.error);
  return reponse?.data ?? null;
}

/** Empreinte du livrable app validé (et publié) d'une version, ou null. */
export async function lireEmpreinteValidee(consultationId, { db = defaultDb } = {}) {
  if (!db?.from) throw new SuiviErreur(MESSAGE_RESEAU, { nature: 'reseau' });
  let reponse;
  try {
    reponse = await db.from('suivi_validations').select('app_artifact_sha256').eq('consultation_id', consultationId).maybeSingle();
  } catch (error) {
    throw traduireErreurSuivi(error);
  }
  if (reponse?.error) throw traduireErreurSuivi(reponse.error);
  return reponse?.data?.app_artifact_sha256 || null;
}

export async function preparerBrouillon({ clientId, motif, rdvOrdinal = null, syntheseId = null, mgdAdoptionId = null }, { db = defaultDb } = {}) {
  if (!SUIVI_MOTIFS.includes(motif)) throw new SuiviErreur('Choisis d’abord pourquoi tu prépares cette version.');
  return uneLigne(await appeler(db, 'suivi_preparer_brouillon', {
    p_client_id: clientId,
    p_motif: motif,
    p_rdv_ordinal: motif === 'rendez_vous' ? rdvOrdinal : null,
    p_synthese_id: syntheseId,
    p_mgd_plan_adoption_id: mgdAdoptionId,
  }));
}

export async function enregistrerBrouillon({ consultationId, revision, changes }, { db = defaultDb } = {}) {
  if (!changes || Object.keys(changes).length === 0) {
    throw new SuiviErreur('Rien à enregistrer.', { nature: 'vide' });
  }
  return uneLigne(await appeler(db, 'suivi_enregistrer_brouillon', {
    p_consultation_id: consultationId,
    p_expected_revision: revision,
    p_changes: changes,
  }));
}

/**
 * Abandonner. Un brouillon de l'ancien écran pas encore repris par le
 * nouveau suivi est d'abord repris (enregistrement neutre de la note
 * interne), puis abandonné : une seule action pour Anissa.
 * Un plan vide empêcherait la reprise (le serveur refuse un plan vide) : il
 * est alors repris avec le plan de la version précédente, puisque le
 * brouillon disparaît aussitôt.
 * Si l'abandon échoue après la reprise, l'erreur porte la ligne reprise
 * (`erreur.ligne`) : l'écran repart de sa nouvelle révision.
 */
export async function abandonnerBrouillon(brouillon, { db = defaultDb } = {}) {
  let ligne = brouillon;
  if (!ligne?.suivi_motif) {
    const changes = { notes_for_coach: typeof ligne.notes_for_coach === 'string' ? ligne.notes_for_coach : '' };
    if (!String(ligne.nutrition_plan || '').trim()) {
      const precedente = ligne.previous_consultation_id
        ? await lireVersion(ligne.previous_consultation_id, { db }).catch(() => null)
        : null;
      if (!String(precedente?.nutrition_plan || '').trim()) {
        throw new SuiviErreur(
          'Ce brouillon de l’ancien écran n’a pas de plan : écris une ligne dans « Plan », puis abandonne-le.',
          { nature: 'refus' },
        );
      }
      changes.nutrition_plan = precedente.nutrition_plan;
    }
    ligne = await enregistrerBrouillon({ consultationId: ligne.id, revision: ligne.followup_edit_revision, changes }, { db });
  }
  try {
    return await appeler(db, 'suivi_abandonner_brouillon', {
      p_consultation_id: ligne.id,
      p_expected_revision: ligne.followup_edit_revision,
    });
  } catch (error) {
    if (ligne !== brouillon) error.ligne = ligne;
    throw error;
  }
}

export async function validerVersion({ consultationId, revision, contentSha256, appArtifactSha256 = null, canaux, verdict, renderInputsSha256, override = null }, { db = defaultDb } = {}) {
  return uneLigne(await appeler(db, 'suivi_valider_version', {
    p_consultation_id: consultationId,
    p_expected_revision: revision,
    p_content_sha256: contentSha256,
    p_app_artifact_sha256: appArtifactSha256,
    p_canaux: canaux,
    p_verdict: verdict,
    p_render_inputs_sha256: renderInputsSha256,
    p_override: override,
  }));
}

export async function verifierPublicationPossible({ validationId, canal }, { db = defaultDb } = {}) {
  return appeler(db, 'suivi_verifier_publication_possible', {
    p_validation_id: validationId,
    p_canal: canal,
  });
}

export async function marquerPubliee({ validationId, appPlanId, publishedVersion, artifactSha256, publishedAt }, { db = defaultDb } = {}) {
  return uneLigne(await appeler(db, 'suivi_marquer_publiee', {
    p_validation_id: validationId,
    p_app_plan_id: appPlanId,
    p_published_version: publishedVersion,
    p_artifact_sha256: artifactSha256,
    p_published_at: publishedAt,
  }));
}

export async function marquerRemiseDocument({ validationId, documentSha256, format = 'pdf', canal }, { db = defaultDb } = {}) {
  return uneLigne(await appeler(db, 'suivi_marquer_remise_document', {
    p_validation_id: validationId,
    p_document_sha256: documentSha256,
    p_format: format,
    p_canal: canal,
  }));
}

export async function marquerNotifiee({ validationId, statut }, { db = defaultDb } = {}) {
  return uneLigne(await appeler(db, 'suivi_marquer_notifiee', {
    p_validation_id: validationId,
    p_statut: statut,
  }));
}

/** Le brouillon ouvert (dernière version non terminée), ou null. */
export function brouillonOuvert(versions) {
  const derniere = Array.isArray(versions) && versions.length ? versions[versions.length - 1] : null;
  return derniere?.etat === 'brouillon' && derniere.motif !== 'initial' ? derniere : null;
}

/** Libellé court d'une version : « V3 ». */
export function nomVersion(version) {
  return version?.rang ? `V${version.rang}` : 'V?';
}
